
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'

import { ConfigState, Erg } from 'shared/state'
import { ConfigModule, ConfigField, ConfigControl, Collection } from 'shared/types'
import { slugify } from 'shared/util'
import * as configModules from 'shared/config'

import PanelDisplay from './ConfigPanel.vue'

interface ConfigPanel {
	label: string
	fields: ConfigControl[]
	subPanels?: ConfigPanel[]
}

@Component({ components: { PanelDisplay } })
export default class Config extends Vue {

	@Prop() config!: ConfigState
	@Prop({ default: '' }) page!: string
	@Prop({ default: false }) loggedIn!: boolean

	nav = ''
	subNavs: Record<string, string> = {}

	slugify = slugify

	@Watch('panels', { immediate: true })
	panelsChanged() {
		if(!this.nav) {
			this.nav = this.panels.length ? this.panels[0].label : ''
		}
	}
	@Watch('nav', { immediate: true })
	navChanged() {
		this.$emit('nav', this.nav)
	}

	setSubNav(panelLabel: string, subPanelLabel: string) {
		this.$set(this.subNavs, panelLabel, subPanelLabel)
	}

	isActiveSubPanel(panelLabel: string, subPanelLabel: string): boolean {
		const active = this.subNavs[panelLabel]
		if(!active) {
			const panel = this.panels.find(p => p.label === panelLabel)
			return panel?.subPanels?.[0]?.label === subPanelLabel
		}
		return active === subPanelLabel
	}

	get duringRace() {
		return Erg.status === 'race running'
	}

	get isParticipating() {
		return this.$route.path.includes('participant')
	}

	get isSpectator() {
		return this.$route.path.includes('spectator')
	}
	get role() {
		return this.isParticipating ? 'participant' : this.isSpectator ? 'spectator' : ''
	}

	get className() {
		const classes = [
			'config',
			slugify(this.nav),
			this.page + '-config',
			this.role
		]
		if(this.loggedIn) { classes.push('loggedIn') }
		if(this.duringRace) { classes.push('duringRace') }
		return classes.join(' ')
	}

	get panels(): ConfigPanel[] {
		const panels: Collection<ConfigPanel> = {}
		const groups: Collection<ConfigControl> = {}
		// subTabFields[panelName][subTabName] = fields[]
		const subTabFields: Record<string, Record<string, ConfigControl[]>> = {}
		// subTabGroups[panelName][subTabName][groupName] = group control
		const subTabGroups: Record<string, Record<string, Record<string, ConfigControl>>> = {}

		Object.keys(configModules).forEach(modName => {
			const mod = configModules[modName] as ConfigModule<any>
			if(mod.page && mod.page !== this.page) { return }

			Object.keys(mod.fields).forEach(fieldName => {
				const field = mod.fields[fieldName]
				if(!this.shouldBeVisible(field)) { return }

				const panelName = field.panel || mod.panel
				if(!panels[panelName]) {
					panels[panelName] = { label: panelName, fields: [] }
				}

				const control = { ...field.control! }
				if(!control.name) {
					control.name = `${modName}.${fieldName}`
				}

				const subTabName = field.subTab && (!field.subTabPages || field.subTabPages.includes(this.page))
					? field.subTab
					: null
				if(subTabName) {
					if(!subTabFields[panelName]) { subTabFields[panelName] = {} }
					if(!subTabFields[panelName][subTabName]) { subTabFields[panelName][subTabName] = [] }

					if(field.group) {
						if(!subTabGroups[panelName]) { subTabGroups[panelName] = {} }
						if(!subTabGroups[panelName][subTabName]) { subTabGroups[panelName][subTabName] = {} }
						if(!subTabGroups[panelName][subTabName][field.group]) {
							subTabGroups[panelName][subTabName][field.group] = {
								component: 'controlGroup',
								label: this.$t(field.group),
								options: []
							}
						}
						subTabGroups[panelName][subTabName][field.group].options!.push(control)
					} else {
						subTabFields[panelName][subTabName].push(control)
					}
					return
				}

				if(field.group) {
					if(!groups[field.group]) {
						groups[field.group] = {
							component: 'controlGroup',
							label: this.$t(field.group),
							options: [],
							name: panelName
						}
					}
					groups[field.group].options!.push(control)
					return
				}

				panels[panelName].fields.push(control)
			})
		})

		for(const groupName of Object.keys(groups)) {
			const group = groups[groupName]
			panels[group.name!].fields.push(group)
			group.name = slugify(groupName)
		}

		for(const panelName of Object.keys(subTabGroups)) {
			for(const subTabName of Object.keys(subTabGroups[panelName])) {
				for(const groupName of Object.keys(subTabGroups[panelName][subTabName])) {
					const group = subTabGroups[panelName][subTabName][groupName]
					subTabFields[panelName][subTabName].push({ ...group, name: slugify(groupName) })
				}
			}
		}

		return Object.keys(panels)
			.filter(name => panels[name].fields.length || subTabFields[name])
			.map(name => {
				const panel = panels[name]
				const spByTab = subTabFields[name]
				if(spByTab && Object.keys(spByTab).length) {
					const generalSubPanel: ConfigPanel = { label: 'General', fields: panel.fields }
					const otherSubPanels: ConfigPanel[] = Object.keys(spByTab).map(spName => ({
						label: spName,
						fields: spByTab[spName]
					}))
					return { ...panel, fields: [], subPanels: [generalSubPanel, ...otherSubPanels] }
				}
				return panel
			})
	}

	private shouldBeVisible(field: ConfigField<any>) {
		const show = !field.hideDuringRace || !this.duringRace

		if (this.isParticipating) {
			return !!field.showToParticipant && show
		}

		if (this.isSpectator) {
			return !!field.showToSpectator && show
		}
		const name = this.$route.name || ''
		const cams = field.cams || 0
		const routeCameras = name === 'split' ? 3 : parseInt(name.replace(/^\D+/g, '')) || 0
		const isVideoSourceField = field.cams && field?.cams <= routeCameras

		return (
			!!field.control &&
			(!field.private || this.loggedIn) &&
			!field.participantOnly &&
			show &&
			(cams === 0 || isVideoSourceField)
		)
	}

}
